import { useToken } from '@/shared';
import { useBaseUrl } from '@/shared/lib/url';
import { useWebSocket } from '@vueuse/core';
import { defineStore, storeToRefs } from 'pinia';
import { ref, watch, watchEffect } from 'vue';
import { Mortgage } from '@/entities/mortgage';
import { Akbars, Chatmessage, ReportsAsync } from 'simpleloan_api';
import { useAuthModel } from '@/features/auth';
import { Metalinvest } from '@/entities/metalinvest/model/type';

export type MortgageSocketCreate = {
  _type: 'mortgage_create';
  _data: {
    app: Mortgage;
  };
};

export type MortgageSocketUpdate = {
  _type: 'mortgage_update';
  _data: {
    app: Mortgage;
  };
};

export type MortgageSocketBankStatus = {
  _type: 'mortgage_bank_status';
  _data: {
    app: Mortgage;
  };
};

export type WebsocketAsyncReport = {
  _data: ReportsAsync;
  _type: 'async_report';
};

export type WebSocketAkbarsEvents =
  | 'akbars_app'
  | 'akbars_scans'
  | 'akbars_processing'
  | 'akbars_loaner_approving'
  | 'akbars_printforms'
  | 'akbars_pledge_approving'
  | 'akbars_issuing'
  | 'akbars_end'
  | 'akbars_cancel'
  | 'checkpassport_app'
  | 'checkpassport_cancel';

export type WebSocketAkbars = {
  _data: Akbars;
  _type: WebSocketAkbarsEvents;
};

export type WebSocketChatmessageRead = {
  _type: 'chatmessage_read';
  _data: Chatmessage;
};

export type WebSocketChatmessageCreate = {
  _type: 'chatmessage_create';
  _data: Chatmessage;
};

export type WebSocketChatmessageDelete = {
  _type: 'chatmessage_delete';
  _data: Chatmessage;
};

export type WebSocketMetalinvest = {
  _type: `metalinvest_${string}`;
  _data: Metalinvest;
};

export type WebsocketData =
  | MortgageSocketCreate
  | MortgageSocketUpdate
  | MortgageSocketBankStatus
  | WebsocketAsyncReport
  | WebSocketAkbars
  | WebSocketChatmessageCreate
  | WebSocketChatmessageDelete
  | WebSocketChatmessageRead
  | WebSocketMetalinvest;

export const useWebsocketStore = defineStore('websocket', () => {
  const authModel = useAuthModel();
  const { isAuthorized } = storeToRefs(authModel);
  const wsHostname = new URL(useBaseUrl()).hostname;

  const buildWssUrl = (token: string | null) => `wss://${wsHostname}:44302/api/v1/ws/?token=${token ?? ''}`;

  const wssUrl = ref(buildWssUrl(useToken()));
  let inactivityTimer: ReturnType<typeof setTimeout> | null = null;

  const { status, data, send, open, close, ws } = useWebSocket(wssUrl, {
    immediate: true,
    autoReconnect: {
      delay: 5000,
      retries: 5,
      onFailed: () => alert('Failed to connect WebSocket')
    },
    onDisconnected: socket => {
      console.warn('WebSocket disconnected. readyState:', socket.readyState);
    },
    onError: () => console.log('websocket error'),
    onConnected: () => {
      console.log('websocket connect');
      resetInactivityTimer(); // Сбрасываем таймер при подключении
    }
  });

  watch(ws, newWs => {
    if (newWs) {
      newWs.onclose = (event: CloseEvent) => {
        console.warn('Сокет закрыт:', event.code, event.reason);

        // JWT истёк или невалиден
        if (event.code === 4001 || event.code === 1008) {
          console.log('Просрочен токен — переподключение токена');
          changeWssUrl();
          setTimeout(open, 1000);
        }

        // Сетевой обрыв
        if (event.code === 1006) {
          console.log('Потеря соединения — переподключение...');
          setTimeout(open, 2000);
        }
      };
    }
  });

  const message = ref<WebsocketData | null>(null);

  // реакция на обновление токена
  watchEffect(() => {
    const token = useToken();

    wssUrl.value = buildWssUrl(token);
  });

  watch(data, newValue => {
    if (!newValue) return;

    try {
      const parsed = JSON.parse(newValue);
      message.value = parsed;
      resetInactivityTimer();
    } catch (err) {
      console.error('Ошибка при парсинге WebSocket данных:', err);
    }
  });

  watch(
    isAuthorized,
    newIsAuthorized => {
      if (newIsAuthorized) {
        changeWssUrl();
        open();
      } else {
        close();
      }
    },
    { immediate: true }
  );

  function changeWssUrl() {
    wssUrl.value = buildWssUrl(useToken());
  }

  function resetInactivityTimer() {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }

    inactivityTimer = setTimeout(() => {
      if (isAuthorized.value) {
        console.log('WebSocket inactivity detected. Reconnecting...');
        close();
        setTimeout(open, 1000); // Даем небольшую паузу перед переподключением
      }
    }, 5 * 60 * 1000); // 5 минут
  }

  return { status, message, send, open, close };
});
